<template>
  <div
    data-toast-anchor
    class="w-full flex bg-white"
    :class="{
      'justify-between items-center h-24 py-2 px-4 sm:px-7 border-t border-neutral-200': !isLargeScreenLayout,
      'flex-col py-4 px-5 h-full max-h-52 overflow-hidden': isLargeScreenLayout,
    }"
  >
    <div v-if="isLargeScreenLayout">
      <div
        v-if="hasQuote && hasAvailability"
        class="flex flex-col"
      >
        <vetster-skeleton-quote-total
          v-if="isQuoteLoading || isPartnerLoading"
          is-large-screen-layout
        />
        <template v-else>
          <div class="flex justify-between mb-4">
            <div class="flex flex-col">
              <span class="text-3xl font-title">{{ $t('app.total') }}</span>
              <span
                v-if="!isSubscription && !hidePriceForSearchPet"
                class="text-neutral-500 text-sm"
              >
                {{ taxLabel }}
              </span>
            </div>
            <div class="text-right">
              <vetster-discount
                container-class="justify-end"
                label-class="text-right text-neutral-900 font-semibold text-xl"
              >
                <template v-if="!hidePriceForSearchPet && !hasAutoshipPromotion">
                  <span class="text-neutral-900 font-bold text-3xl font-title">
                    {{ getQuotePrice }}
                  </span>
                </template>
              </vetster-discount>
              <span class="text-neutral-700">
                {{ getDate }}
              </span>
            </div>
          </div>
        </template>
      </div>
      <div
        v-else
        class="flex flex-1"
      >
        <div
          v-if="hasError"
          class="flex-1 text-red-600 flex items-center justify-center mb-2"
        >
          <span class="text-sm">{{ getError }}</span>
        </div>
        <vetster-skeleton-quote-total
          v-else-if="isQuoteLoading || !pageReady"
          class="flex-1"
          is-large-screen-layout
        />
        <div
          v-else
          class="text-neutral-600 flex flex-1 items-center justify-center mb-4 text-lg"
        >
          <span>{{ $t('quote.choose-a-different-date') }}</span>
        </div>
      </div>
    </div>
    <div
      v-else
      class="flex flex-1"
    >
      <div
        v-if="hasQuote"
        class="flex flex-col"
      >
        <vetster-skeleton-quote-total v-if="isQuoteLoading" />
        <div
          v-else-if="hasNoAvailability && isQuotePanel"
          class="text-neutral-600 flex items-center justify-center"
        >
          <span>{{ $t('quote.choose-a-different-date') }}</span>
        </div>
        <template v-else>
          <div>
            <vetster-discount label-class="text-neutral-900 font-semibold text-xl">
              <template v-if="!hidePriceForSearchPet && !hasAutoshipPromotion">
                <span
                  class="text-neutral-900 font-bold"
                  :class="isSubscription ? 'text-base' : 'text-xl'"
                >
                  {{ getQuotePrice }}
                </span>
                <span
                  v-if="!isSubscription && !hidePriceForSearchPet"
                  class="text-neutral-500 text-sm"
                >
                  {{ taxLabel }}
                </span>
              </template>
            </vetster-discount>
          </div>
          <button
            class="text-neutral-500 text-left"
            :class="{ 'underline text-sm': !isQuotePanel }"
            type="button"
            @click.stop="$emit('toggle-quote-panel')"
          >
            {{ getDate }}
          </button>
        </template>
      </div>
      <div
        v-else
        class="flex flex-1"
      >
        <div
          v-if="hasError"
          class="text-red-600 flex items-center justify-center mr-2"
        >
          <span class="text-sm">{{ getError }}</span>
        </div>
        <vetster-skeleton-quote-total v-else-if="isQuoteLoading || !pageReady" />
      </div>
    </div>
    <div class="flex flex-1 flex-col">
      <vetster-button
        id="continue-booking-btn"
        type="button"
        :aria-label="$t('app.book-now')"
        :disabled="!hasQuote || isQuoteLoading || !pageReady || (hasNoAvailability && isQuotePanel)"
        :is-loading="isQuoteLoading"
        :variant="buttonVariant"
        :size="isLargeScreenLayout ? 'lg' : 'base'"
        class="w-auto"
        @click="$emit('checkout')"
      >
        {{ buttonLabel }}
      </vetster-button>
      <span class="text-center text-xs lg:text-sm text-neutral-500 mt-1.5">
        {{ $t('quote.you-wont-be-charged-yet') }}
      </span>
    </div>
  </div>
</template>

<script>
import { DateTime } from 'luxon';
import { getCurrentInstance } from 'vue';
import { mapGetters, mapState } from 'vuex';

import VetsterSkeletonQuoteTotal from '@/brand-components/VetsterSkeletonQuoteTotal';
import VetsterDiscount from '@/components/VetsterDiscount';
import useCheckoutHelpers from '@/composables/use-checkout-helpers';
import useTimeslotHelpers from '@/composables/use-timeslot-helpers';
import { formatMoneyRounded } from '@/helpers/format';
import HasConversionRate from '@/mixins/HasConversionRate';
import HasYearlySubscriptionPlan from '@/mixins/HasYearlySubscriptionPlan';

export default {
  name: 'VetsterQuoteTotal',

  components: {
    VetsterDiscount,
    VetsterSkeletonQuoteTotal,
  },

  mixins: [
    HasConversionRate,
    HasYearlySubscriptionPlan,
  ],

  props: {
    isQuotePanel: {
      type: Boolean,
      default: false,
    },
    isLargeScreenLayout: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const root = getCurrentInstance().proxy.$root;
    const { buttonDate } = useTimeslotHelpers();
    const {
      isSubscription,
      showPaymentOption,
    } = useCheckoutHelpers(root);

    return {
      buttonDate,
      isSubscription,
      showPaymentOption,
    };
  },

  computed: {
    ...mapGetters('quote', [
      'hasQuote',
      'getQuote',
      'getError',
      'hasAvailability',
      'hasError',
      'hasNoAvailability',
      'isPartnerLoading',
      'isQuoteLoading',
      'isStandardPricing',
    ]),
    ...mapState('quote', ['pageReady']),
    ...mapGetters('search', ['hidePriceForSearchPet']),
    ...mapGetters('promoCodes', ['hasAutoshipPromotion']),
    amount() {
      return this.convertCurrency(this.getQuote.total.amount);
    },
    buttonLabel() {
      if (this.isLargeScreenLayout) {
        return this.$t('quote.continue-booking');
      }

      return this.$t('app.book-now');
    },
    buttonVariant() {
      return this.isBookingCtaColorExperiment ? 'ocean' : 'accentPink';
    },
    getDate() {
      const startTime = DateTime.fromISO(this.getQuote.start_at, { zone: this.timezone });
      const endTime = DateTime.fromISO(this.getQuote.end_at, { zone: this.timezone });
      const buttonDate = this.buttonDate(startTime, this.timezone, true);

      const dateStartTime = `${buttonDate} ${this.$t('common.at')} ${startTime.toFormat('t')}`;
      const dateRangeTime = `${dateStartTime} - ${endTime.toFormat('t')}`;

      return this.isStandardPricing ? dateRangeTime : dateStartTime;
    },
    getQuotePrice() {
      if (this.isSubscription) {
        if (this.monthlyAmount && this.showPaymentOption) {
          return `${this.formattedMonthlyAmount}/${this.$t('membership-subscriptions.monthly-abbreviation')}`;
        }

        return this.$n(formatMoneyRounded(this.amount), {
          style: 'currency',
          currency: this.getConversionCurrency || this.getQuote.total.currency,
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        });
      }

      // TODO: Remove this when the experiment is over
      return this.$n(formatMoneyRounded(this.amount), {
        style: 'currency',
        currency: this.getConversionCurrency || this.getQuote.total.currency,
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      });
    },
    isBookingCtaColorExperiment() {
      return this.$statsig.ready
        && this.$statsig.checkGate('search_continue_booking_cta')
        && this.$statsig.getExperiment('expt_search_continue_booking_cta').get('value', false);
    },
    taxLabel() {
      return this.isStandardPricing
        ? this.$t('quote.incl-tax')
        : this.$t('quote.including-taxes-and-fees-alt');
    },
  },

  methods: {
    formatMoneyRounded,
  },
};
</script>
